import { supabase } from '../lib/supabase';

export interface Group {
  id: string;
  name: string;
  description: string | null;
  createdBy: string;
  subscriptionPlanId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroupInput {
  name: string;
  description?: string;
  companyName?: string;
  companyLogoUrl?: string;
}

export type GroupMemberRole = 'admin' | 'clerk' | 'member';

class GroupService {
  async createGroup(input: CreateGroupInput): Promise<Group> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { subscriptionService } = await import('./subscriptionService');
      const isExpired = await subscriptionService.isSubscriptionExpired(user.user.id);
      if (isExpired) {
        const message = await subscriptionService.getSubscriptionExpiryMessage(user.user.id);
        throw new Error(message);
      }

      try {
        const { data: canCreate, error: limitError } = await supabase.rpc('can_create_group', {
          user_id_param: user.user.id
        });

        if (limitError) throw limitError;

        if (!canCreate?.allowed) {
          throw new Error(canCreate?.reason || 'Cannot create group. Please check your subscription limits.');
        }
      } catch (e: any) {
        if (e.message?.includes('Failed to fetch') || e.message?.includes('network')) {
          console.log('Offline mode - group creation allowed if subscription active');
        } else {
          throw e;
        }
      }

      const { data, error } = await supabase
        .from('groups')
        .insert({
          name: input.name,
          description: input.description || null,
          company_name: input.companyName || null,
          company_logo_url: input.companyLogoUrl || null,
          created_by: user.user.id,
          subscription_plan_id: null,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: data.id,
          user_id: user.user.id,
          role: 'admin',
        });

      if (memberError) {
        await supabase.from('groups').update({ is_active: false }).eq('id', data.id);
        throw new Error(`Failed to add creator to group: ${memberError.message}. Group creation was rolled back.`);
      }

      try {
        const { data: currentProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.user.id)
          .single();

        if (!profileError && currentProfile &&
            (currentProfile.role === 'member' || currentProfile.role === 'group_member')) {
          await supabase
            .from('user_profiles')
            .update({ role: 'group_creator' })
            .eq('id', user.user.id);
        }
      } catch (networkError) {
        console.warn('Role update skipped:', networkError);
      }

      return this.mapGroup(data);
    } catch (error: any) {
      throw new Error(`Failed to create group: ${error.message}`);
    }
  }

  async getGroups(): Promise<Group[]> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const [{ data: createdGroups, error: createdError }, { data: adminMemberships, error: membershipsError }] =
        await Promise.all([
          supabase.from('groups').select('id').eq('created_by', user.user.id).eq('is_active', true),
          supabase.from('group_members').select('group_id, role').eq('user_id', user.user.id).in('role', ['admin', 'group_admin', 'clerk']),
        ]);

      if (createdError) throw createdError;
      if (membershipsError) throw membershipsError;

      const groupIds = Array.from(
        new Set([
          ...(createdGroups || []).map((g: any) => g.id),
          ...(adminMemberships || []).map((m: any) => m.group_id),
        ]),
      ).filter(Boolean);

      if (groupIds.length === 0) return [];

      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .in('id', groupIds)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (groupsError) throw groupsError;

      return (groups || []).map(this.mapGroup);
    } catch (error: any) {
      throw new Error(`Failed to fetch groups: ${error.message}`);
    }
  }

  async getGroupById(groupId: string): Promise<Group | null> {
    try {
      const { data, error } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return this.mapGroup(data);
    } catch (error: any) {
      throw new Error(`Failed to fetch group: ${error.message}`);
    }
  }

  async updateGroup(
    groupId: string,
    updates: Partial<CreateGroupInput & { companyName?: string; companyLogoUrl?: string }>
  ): Promise<Group> {
    try {
      const updateData: any = { updated_at: new Date().toISOString() };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.companyName !== undefined) updateData.company_name = updates.companyName;
      if (updates.companyLogoUrl !== undefined) updateData.company_logo_url = updates.companyLogoUrl;

      const { data, error } = await supabase
        .from('groups')
        .update(updateData)
        .eq('id', groupId)
        .select()
        .single();

      if (error) throw error;

      return this.mapGroup(data);
    } catch (error: any) {
      throw new Error(`Failed to update group: ${error.message}`);
    }
  }

  async deleteGroup(groupId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('groups')
        .update({ is_active: false })
        .eq('id', groupId);

      if (error) throw error;
    } catch (error: any) {
      throw new Error(`Failed to delete group: ${error.message}`);
    }
  }

  async getCurrentUserGroupMemberRole(
    groupId: string,
    userId: string
  ): Promise<GroupMemberRole | null> {
    const { data, error } = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data?.role) return null;
    const role = data.role as string;
    if (role === 'admin' || role === 'clerk' || role === 'member') return role;
    return null;
  }

  async canRequestLoanOnBehalf(groupId: string, userId: string): Promise<boolean> {
    const role = await this.getCurrentUserGroupMemberRole(groupId, userId);
    if (role === 'clerk' || role === 'admin') return true;
    return this.isGroupAdminForLoan(groupId, userId);
  }

  async isGroupAdminForLoan(groupId: string, userId: string): Promise<boolean> {
    const memberRole = await this.getCurrentUserGroupMemberRole(groupId, userId);
    if (memberRole === 'admin') return true;

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    const platformRole = profile?.role as string | undefined;
    if (
      platformRole === 'group_admin' ||
      platformRole === 'super_admin' ||
      platformRole === 'group_creator'
    ) {
      if (memberRole) return true;
    }

    const { data: group } = await supabase
      .from('groups')
      .select('created_by')
      .eq('id', groupId)
      .maybeSingle();

    return group?.created_by === userId;
  }

  async getUserGroups(userId: string): Promise<Array<{ id: string; name: string; currency: string }>> {
    const { data, error } = await supabase
      .from('group_members')
      .select('group_id, groups:group_id(id, name, currency)')
      .eq('user_id', userId);

    if (error) throw error;

    return (data || [])
      .map((row: any) => row.groups)
      .filter(Boolean)
      .map((g: any) => ({
        id: g.id as string,
        name: g.name as string,
        currency: (g.currency as string) || 'VT',
      }));
  }

  async getGroupMembersWithProfiles(
    groupId: string
  ): Promise<Array<{ user_id: string; full_name: string; email?: string; role: string }>> {
    const { data, error } = await supabase
      .from('group_members')
      .select('user_id, role, user_profiles:user_id(id, full_name, email)')
      .eq('group_id', groupId)
      .order('role', { ascending: true });

    if (error) throw error;

    return (data || []).map((row: any) => ({
      user_id: row.user_id as string,
      full_name: row.user_profiles?.full_name || row.user_profiles?.email || 'Unknown',
      email: row.user_profiles?.email,
      role: row.role as string,
    }));
  }

  async isLendingEnabled(groupId: string): Promise<boolean> {
    const { data } = await supabase
      .from('groups')
      .select('lending_enabled')
      .eq('id', groupId)
      .maybeSingle();

    return data?.lending_enabled !== false;
  }

  private mapGroup(data: any): Group {
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      createdBy: data.created_by,
      subscriptionPlanId: data.subscription_plan_id,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const groupService = new GroupService();
